import React, { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { Button } from '@shared/components/bootstrap';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { Dialog as ConfirmationDialog } from '@admin/components/confirmation';
import { isAutoDispatchRouteLockActive } from '@admin/components/dispatcher/dispatcher_auto_dispatch_route_lock';
import { DispatcherFilters } from '@admin/components/dispatcher/dispatcher_filters';
import { DispatcherMap } from '@admin/components/dispatcher/dispatcher_map';
import type { DispatcherMapDepot } from '@admin/components/dispatcher/dispatcher_map_plotting';
import {
  DispatcherOrphanShiftDialog,
  formatDispatcherWorkDateLabel,
} from '@admin/components/dispatcher/dispatcher_orphan_shift_dialog';
import { DispatcherRequestButton } from '@admin/components/dispatcher/dispatcher_request_button';
import { DispatcherRouteColumns } from '@admin/components/dispatcher/dispatcher_route_columns';
import { hasPublishedDispatches } from '@admin/components/dispatcher/dispatcher_save_payload';
import { DispatcherTimelineLegend } from '@admin/components/dispatcher/dispatcher_timeline_legend';
import { DispatcherUndispatchedDrawer } from '@admin/components/dispatcher/dispatcher_undispatched_drawer';
import { useDispatcherData } from '@admin/components/dispatcher/hooks/use_dispatcher_data';
import { useDispatcherEditor } from '@admin/components/dispatcher/hooks/use_dispatcher_editor';
import { useDispatcherResources } from '@admin/components/dispatcher/hooks/use_dispatcher_resources';
import { useDispatcherSave } from '@admin/components/dispatcher/hooks/use_dispatcher_save';
import { DispatcherManageButton } from '@admin/components/dispatcher/manage_button';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { useTitle } from '@admin/hooks/use_title';
import { ADMIN_DISPATCH } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useAutoDispatchToggleAllowedQuery, useRegionsSelectorQuery } from '@admin/schema';

const LOAD_ERROR_MESSAGE = 'Unable to load dispatcher data. Please try again.';

type DispatcherMode = 'viewing' | 'modifying';

export const DispatcherPage: React.FC = () => {
  useTitle('Dispatcher');
  const [mode, setMode] = useState<DispatcherMode>('viewing');
  const [regionCenter, setRegionCenter] = useState<{ latitude: number; longitude: number } | null>(null);
  const { data: regionData, loading: regionsLoading } = useRegionsSelectorQuery({ client });
  const regions = useMemo(() => regionData?.regions ?? [], [regionData?.regions]);
  const [regionID, setRegionID] = useURLSearchParam('region_id');
  const [date, setDate] = useURLSearchParam('date');

  const filters = useMemo(
    () => ({
      region_id: regionID ? Number(regionID) : undefined,
      date: date,
    }),
    [date, regionID],
  );

  const { data, loading: dataLoading, error: dataError, filtering, reload } = useDispatcherData(filters);
  const { resources, loading: resourcesLoading, error: resourcesError } = useDispatcherResources();
  const { data: toggleData } = useAutoDispatchToggleAllowedQuery({
    client,
    variables: { regionID: regionID || '', date: date || '' },
    skip: !regionID || !date,
  });
  const routeLockActive = isAutoDispatchRouteLockActive(toggleData?.autoDispatchToggleAllowed);
  const modifying = mode === 'modifying';

  const selectedRegion = useMemo(() => {
    if (!regionID) {
      return undefined;
    }

    const match = regions.find((region) => region.id === regionID);
    if (!match) {
      return undefined;
    }

    return { id: Number(regionID), tz: match.tz };
  }, [regionID, regions]);

  const editor = useDispatcherEditor({
    data,
    resources,
    regionId: regionID ? Number(regionID) : undefined,
    regionTz: selectedRegion?.tz,
    modifying,
    routeLockActive,
  });

  const loading = filtering && (dataLoading || resourcesLoading);
  const error = dataError ?? resourcesError;

  const handleManage = useCallback(() => {
    editor.beginEditing();
    setMode('modifying');
  }, [editor]);

  const handleSaveSuccess = useCallback(() => {
    editor.resetEditor();
    setMode('viewing');
    reload();
  }, [editor, reload]);

  const {
    saving,
    save,
    orphanShift,
    keepOrphanShifts,
    removeOrphanShifts,
    resolvingOrphanShifts,
    confirmation: saveConfirmation,
  } = useDispatcherSave({
    onSaved: handleSaveSuccess,
  });

  const published = useMemo(
    () => (editor.activeData ? hasPublishedDispatches(editor.activeData.dispatches) : false),
    [editor.activeData],
  );

  const handleSave = useCallback(
    (publish: boolean) => {
      if (!editor.activeData || !data || !regionID || !date) {
        return;
      }

      void save({
        editorData: editor.activeData,
        baselineData: data,
        publish,
        date: date,
        regionId: Number(regionID),
      });
    },
    [data, date, editor.activeData, regionID, save],
  );

  const handleCancel = useCallback(() => {
    editor.resetEditor();
    setMode('viewing');
    reload();
  }, [editor, reload]);

  useEffect(() => {
    if (!regionID) {
      setRegionCenter(null);
      return;
    }

    let cancelled = false;
    void axios
      .get<Array<{ id: number; center?: { latitude: number; longitude: number } }>>('/regions.json')
      .then((response) => {
        if (cancelled) {
          return;
        }

        const region = response.data.find((entry) => entry.id === Number(regionID));
        setRegionCenter(region?.center ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setRegionCenter(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [regionID]);

  return (
    <div className={`dispatcher dispatcher-${mode}`}>
      <div className="text-right mar-btm">
        <CreateTechSupportTicket ticketable={{ feature: 'Dispatcher', displayName: 'Dispatcher' }} />
      </div>

      <DispatcherFilters
        regions={regions.map((region) => ({ id: String(region.id), name: region.name }))}
        regionsLoading={regionsLoading}
        regionID={regionID ?? ''}
        date={date ?? ''}
        disabled={modifying}
        onRegionChange={setRegionID}
        onDateChange={setDate}
        actions={
          filtering ? (
            <>
              <Permission resource={ADMIN_DISPATCH.resource} action={ADMIN_DISPATCH.actions.update} denied={null}>
                <DispatcherManageButton
                  mode={mode}
                  regionID={regionID ?? null}
                  date={date ?? null}
                  onManage={handleManage}
                />
              </Permission>
              {modifying && (
                <span>
                  <Button kind="default" type="button" className="mar-lft" onClick={handleCancel} disabled={saving}>
                    Cancel Changes
                  </Button>
                  {published ? (
                    <Button
                      kind="primary"
                      type="button"
                      className="mar-lft"
                      onClick={() => handleSave(true)}
                      disabled={saving}
                      loading={saving}
                    >
                      Save and Notify
                    </Button>
                  ) : (
                    <>
                      {regionID && date && (
                        <span className="mar-lft">
                          <DispatcherRequestButton date={new Date(date)} regionID={regionID} />
                        </span>
                      )}
                      <Button
                        kind="primary"
                        type="button"
                        className="mar-lft"
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        loading={saving}
                      >
                        Save as Draft
                      </Button>
                      <Button
                        kind="primary"
                        type="button"
                        className="mar-lft"
                        onClick={() => handleSave(true)}
                        disabled={saving}
                        loading={saving}
                      >
                        Save and Notify
                      </Button>
                    </>
                  )}
                </span>
              )}
            </>
          ) : undefined
        }
      />

      {!filtering && (
        <div className="alert alert-info" role="status">
          Select a region and date to load dispatches.
        </div>
      )}

      {filtering && loading && (
        <div className="dispatcher-spinner">
          <Spinner />
        </div>
      )}

      {filtering && error && (
        <div className="alert alert-danger" role="alert">
          {LOAD_ERROR_MESSAGE}
        </div>
      )}

      {filtering && editor.layout && resources && selectedRegion && date && !loading && (
        <div className="dispatcher-wrapper">
          <DispatcherTimelineLegend
            defaultDropZone={editor.defaultDropZone}
            highlightDropZone={editor.highlightDropZone}
          />
          <DispatcherUndispatchedDrawer
            layout={editor.layout}
            mode={mode}
            onDrop={() => editor.handleDrop(null)}
            onDragstart={editor.handleDragStart}
            onDragmove={editor.handleDragMove}
            onDragend={editor.handleDragEnd}
          />

          <div className="dispatcher-main">
            <div className="dispatcher-lists">
              <DispatcherRouteColumns
                layout={editor.layout}
                resources={resources}
                region={selectedRegion}
                date={date}
                mode={mode}
                routeLockActive={routeLockActive}
                fieldShiftAssignments={data?.fieldShiftAssignments ?? []}
                onRemove={editor.removeDispatch}
                onDrop={editor.handleDrop}
                onDropAddRoute={editor.handleDropOnAddRoute}
                onAddRoute={() => editor.addDispatch()}
                onDragstart={editor.handleDragStart}
                onDragmove={editor.handleDragMove}
                onDragend={editor.handleDragEnd}
              />
            </div>
          </div>
        </div>
      )}

      {filtering && editor.layout && resources && !loading && (
        <DispatcherMap
          layout={editor.layout}
          depots={resources.depots as DispatcherMapDepot[]}
          regionCenter={regionCenter}
        />
      )}

      {orphanShift && (
        <DispatcherOrphanShiftDialog
          users={orphanShift.users}
          workDateLabel={formatDispatcherWorkDateLabel(orphanShift.date)}
          resolving={resolvingOrphanShifts}
          onKeep={keepOrphanShifts}
          onRemoveShifts={removeOrphanShifts}
        />
      )}
      {editor.confirmation && <ConfirmationDialog confirmation={editor.confirmation} />}
      {saveConfirmation && <ConfirmationDialog confirmation={saveConfirmation} />}
    </div>
  );
};
